/**
 * Sticky footer component displaying live ETH and SOL prices + social links + token search
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAllTokens } from '../../hooks/useAllTokens';
import { getRelayTokenPrice, getRelayTokenPriceSolana } from '../../hooks/useRelayPrice';
import FeedbackModal from './modals/FeedbackModal';

const FooterContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 48px;
  background: #000000;
  border-top: 1px solid var(--color-border, #333333);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  gap: 16px;
  z-index: 1000;

  @media (max-width: 768px) {
    padding: 0 12px;
    gap: 10px;
    height: 44px;
  }
`;

const PriceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const CoinLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;

const PriceText = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #a1a1aa;

  &:hover {
    background: var(--color-bg-hover, #222222);
    border-color: var(--color-border-light, #333333);
    color: #ffffff;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const SocialIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

const SearchContainer = styled.div`
  flex: 0 0 auto;
  width: 280px;
  position: relative;

  @media (max-width: 768px) {
    width: 180px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 0 12px 0 32px;
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-border-light, #333333);
    background: var(--color-bg-hover, #222222);
  }

  &::placeholder {
    color: #71717a;
  }

  @media (max-width: 768px) {
    height: 28px;
    font-size: 12px;
    padding: 0 10px 0 28px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 14px;
  pointer-events: none;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border: 2px solid currentColor;
    border-radius: 50%;
    position: absolute;
    top: 0;
    left: 0;
  }

  &::after {
    content: '';
    width: 2px;
    height: 5px;
    background: currentColor;
    position: absolute;
    bottom: 0;
    right: 0;
    transform: rotate(-45deg);
    border-radius: 1px;
  }

  @media (max-width: 768px) {
    left: 8px;
    width: 14px;
    height: 14px;
  }
`;

const SearchResultsPanel = styled.div<{ $show: boolean }>`
  position: fixed;
  bottom: 56px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 500px;
  background: var(--color-bg-elevated, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 12px;
  padding: ${props => props.$show ? '16px' : '0'};
  max-height: ${props => props.$show ? '350px' : '0'};
  overflow-y: auto;
  opacity: ${props => props.$show ? '1' : '0'};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
  box-shadow: ${props => props.$show ? '0 -8px 32px rgba(0, 0, 0, 0.5)' : 'none'};

  @media (max-width: 768px) {
    width: 95%;
    max-height: ${props => props.$show ? '280px' : '0'};
    padding: ${props => props.$show ? '12px' : '0'};
    bottom: 52px;
  }
`;

const TokenResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-bg-hover, #222222);
    border-color: var(--color-border-light, #333333);
    transform: translateX(4px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 10px;
    gap: 10px;
  }
`;

const TokenResultLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(168, 85, 247, 0.2);
  border: 1px solid #333333;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const TokenResultInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TokenResultName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #fff;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const TokenResultSymbol = styled.div`
  font-size: 12px;
  color: #9ca3af;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const TokenResultAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  font-family: monospace;
  font-size: 11px;
  color: #a855f7;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(168, 85, 247, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 5px 8px;
  }
`;

const CopyIcon = styled.span`
  font-size: 12px;
  color: #9ca3af;
`;

const NoResultsText = styled.div`
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  padding: 20px;
`;

const FeedbackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #3f3f46;
  border-radius: 6px;
  color: #a1a1aa;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #9535fc;
    color: #9535fc;
    background: rgba(149, 53, 252, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 11px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    /* Just show icon on very small screens */
    span {
      display: none;
    }
  }
`;

const PriceFooter: React.FC = () => {
  const [ethPrice, setEthPrice] = useState<number | null>(null);
  const [solPrice, setSolPrice] = useState<number | null>(null);
  const [footerSearch, setFooterSearch] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Get all tokens for search
  const { allTokens } = useAllTokens();

  // Token addresses
  const WETH_ADDRESS = '0x4200000000000000000000000000000000000006';
  const WSOL_ADDRESS = 'So11111111111111111111111111111111111111112'; // Wrapped SOL address for Relay API (equivalent to WETH for ETH)

  // Filter tokens for search
  const filteredTokens = useMemo(() => {
    if (!footerSearch || footerSearch.length < 2) return [];
    const search = footerSearch.toLowerCase();
    return allTokens.filter(token =>
      token.name.toLowerCase().includes(search) ||
      token.symbol.toLowerCase().includes(search) ||
      token.contract_address?.toLowerCase().includes(search)
    ).slice(0, 5);
  }, [allTokens, footerSearch]);

  // Fetch prices from Relay Protocol API
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        // Fetch WETH price (for ETH)
        const wethPrice = await getRelayTokenPrice(WETH_ADDRESS);
        if (wethPrice) {
          setEthPrice(wethPrice);
          console.log('[PriceFooter] ETH price:', wethPrice);
        }
      } catch (error) {
        console.error('[PriceFooter] Failed to fetch ETH price:', error);
      }

      try {
        // Fetch SOL price from Relay Protocol API using WSOL (wrapped SOL)
        // Similar to how we use WETH for ETH price
        const solPrice = await getRelayTokenPriceSolana(WSOL_ADDRESS);
        if (solPrice) {
          setSolPrice(solPrice);
          console.log('[PriceFooter] SOL price:', solPrice);
        }
      } catch (error) {
        console.error('[PriceFooter] Failed to fetch SOL price:', error);
      }
    };

    // Fetch immediately
    fetchPrices();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPrices, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price: number | null): string => {
    if (price === null) return 'Loading...';
    // Use 2 decimal places for better precision
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <>
      {/* Search Results Panel - Expands upward */}
      <SearchResultsPanel $show={footerSearch.length >= 2 && filteredTokens.length > 0}>
        {filteredTokens.map((token) => (
          <TokenResultItem key={token.contract_address}>
            <TokenResultLogo
              src={token.logo_url || '/landing-assets/default-token.png'}
              alt={token.symbol}
              onError={(e) => { (e.target as HTMLImageElement).src = '/landing-assets/default-token.png'; }}
            />
            <TokenResultInfo>
              <TokenResultName>{token.name}</TokenResultName>
              <TokenResultSymbol>${token.symbol}</TokenResultSymbol>
            </TokenResultInfo>
            <TokenResultAddress
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(token.contract_address);
                alert('Address copied!');
              }}
              title="Click to copy address"
            >
              {token.contract_address.slice(0, 6)}...{token.contract_address.slice(-4)}
              <CopyIcon>📋</CopyIcon>
            </TokenResultAddress>
          </TokenResultItem>
        ))}
        {footerSearch.length >= 2 && filteredTokens.length === 0 && (
          <NoResultsText>No tokens found matching "{footerSearch}"</NoResultsText>
        )}
      </SearchResultsPanel>

      {/* Footer Bar */}
      <FooterContainer>
        {/* Left Section - ETH & SOL Prices & Social Links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PriceItem>
              <CoinLogo src="/landing-assets/ETH_logo.png" alt="ETH" />
              <PriceText>{formatPrice(ethPrice)}</PriceText>
            </PriceItem>
            <PriceItem>
              <CoinLogo src="/landing-assets/Solana.png" alt="SOL" style={{ borderRadius: '50%' }} />
              <PriceText>{formatPrice(solPrice)}</PriceText>
            </PriceItem>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SocialLink
              href="https://x.com/mavrkofficial"
              target="_blank"
              rel="noopener noreferrer"
              title="Follow us on X"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </SocialLink>

            <SocialLink
              href="https://github.com/MavrkInk"
              target="_blank"
              rel="noopener noreferrer"
              title="View our GitHub"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            </SocialLink>
            
            {/* Feedback Button */}
            <FeedbackButton onClick={() => setShowFeedbackModal(true)} title="Send us feedback">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>Feedback?</span>
            </FeedbackButton>
          </div>
        </div>

        {/* Right Section - Search */}
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search tokens..."
            value={footerSearch}
            onChange={(e) => setFooterSearch(e.target.value)}
          />
        </SearchContainer>
      </FooterContainer>
      
      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)} 
      />
    </>
  );
};

export default PriceFooter;

